import React, { useEffect, useMemo, useState } from 'react';
import Helmet from 'react-helmet';
import { Portlet, PortletBody, PortletHeader, PortletHeaderToolbar } from "../../partials/content/Portlet";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, Stack, TextField } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { PagedTable, pageStore, TableLevelAction, XDataGrid } from '../../common';
import { dateEquators, integerEquators, StringEquators } from '../../store/constants/pages';
import { createDateField, createNumberDataField, createStringDataField } from '../../util/format';
import { useFitHeight } from '../../../_metronic/layout/Layout';

const DEFAULT_SORT = [
    { id: 'effectiveDate', direction: 'desc' }
];

const useStyles = makeStyles(theme => ({
    button: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
}));

const { usePageStore, useGridFilters } = pageStore('provider-data-landing', {
    sort: DEFAULT_SORT
});

export default function ProviderDataPage({
    intl,
    providerData,
    pending,
    history,
    actions: {
        providerDataLanding,
        searchGetByName,
        searchReset,
        resetPagedTable,
        resetCriteriaWaiting
    }}) {
    useFitHeight();
    const classes = useStyles();
    const [refresh, setRefresh] = useState(Promise.resolve());
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);

    const handleOpen = (rowData) => {
        setSelectedRowData(rowData);
        setOpenDialog(true);
    }

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedRowData(null);
    }

    const fieldDefinitions = [
        { name: 'id', label: 'Id' },
        { name: 'providerCcn', label: 'Provider CCN' },
        { name: 'effectiveDate', label: 'Effective Date' },
        { name: 'nationalProviderIdentifier', label: 'National Provider Identifier' },
        { name: 'bedSize', label: 'Bed Size' },
        { name: 'bundleModel1Discount', label: 'Bundle Model1 Discount' },
        { name: 'capitalCostToChargeRatio', label: 'Capital Cost To Charge Ratio' },
        { name: 'capitalExceptionPaymentRate', label: 'Capital Exception Payment Rate' },
        { name: 'capitalIndirectMedicalEducationRatio', label: 'Capital Indirect Medical Education Ratio' },
        { name: 'capitalPpsPaymentCode', label: 'Capital PPS Payment Code' },
        { name: 'caseMixAdjustedCostPerDischargePpsFacilitySpecificRate', label: 'Case Mix Adjusted Cost Per Discharge PPS Facility Specific Rate' },
        { name: 'caseMixIndex', label: 'Case Mix Index' },
        { name: 'cbsaActualGeographicLocation', label: 'CBSA Actual Geographic Location' },
        { name: 'cbsaStandardizedAmountLocation', label: 'CBSA Standardized Amount Location' },
        { name: 'cbsaWageIndexLocation', label: 'CBSA Wage Index Location' },
        { name: 'censusDivision', label: 'Census Division' },
        { name: 'changeCodeForLugarReclassification', label: 'Change Code for Lugar Reclassification' },
        { name: 'changeCodeWageIndexReclassification', label: 'Change Code Wage Index Reclassification' },
        { name: 'costOfLivingAdjustment', label: 'Cost of Living Adjustment' },
        { name: 'countyCode', label: 'County Code' },
        { name: 'ehrReductionIndicator', label: 'EHR Reduction Indicator' },
        { name: 'exportDate', label: 'Export Date' },
        { name: 'federalPpsBlend', label: 'Federal PPS Blend' },
        { name: 'fiscalYearBeginDate', label: 'Fiscal Year Begin Date' },
        { name: 'fiscalYearEndDate', label: 'Fiscal Year End Date' },
        { name: 'hacReductionParticipantIndicator', label: 'HAC Reduction Participant Indicator' },
        { name: 'hospitalQualityIndicator', label: 'Hospital Quality Indicator' },
        { name: 'hospitalSpecificCapitalRate', label: 'Hospital Specific Capital Rate' },
        { name: 'hrrAdjustment', label: 'HRR Adjustment' },
        { name: 'hrrParticipantIndicator', label: 'HRR Participant Indicator' },
        { name: 'intermediaryNumber', label: 'Intermediary Number' },
        { name: 'internsToBedsRatio', label: 'Interns to Beds Ratio' },
        { name: 'lastUpdated', label: 'Last Updated' },
        { name: 'lowVolumeAdjustmentFactor', label: 'Low Volume Adjustment Factor' },
        { name: 'ltchDppIndicator', label: 'LTCH DPP Indicator' },
        { name: 'medicaidRatio', label: 'Medicaid Ratio' },
        { name: 'medicarePerformanceAdjustment', label: 'Medicare Performance Adjustment' },
        { name: 'msaActualGeographicLocation', label: 'MSA Actual Geographic Location' },
        { name: 'msaStandardizedAmountLocation', label: 'MSA Standardized Amount Location' },
        { name: 'msaWageIndexLocation', label: 'MSA Wage Index Location' },
        { name: 'newCapitalHoldHarmlessRate', label: 'New Capital Hold Harmless Rate' },
        { name: 'newHospital', label: 'New Hospital' },
        { name: 'oldCapitalHoldHarmlessRate', label: 'Old Capital Hold Harmless Rate' },
        { name: 'operatingCostToChargeRatio', label: 'Operating Cost to Charge Ratio' },
        { name: 'passThroughAmountForAllogenicStemCellAcquisition', label: 'Pass-Through Amount for Allogenic Stem Cell Acquisition' },
        { name: 'passThroughAmountForDirectGraduateMedicalEducation', label: 'Pass-Through Amount for Direct Graduate Medical Education' },
        { name: 'passThroughAmountForDirectMedicalEducation', label: 'Pass-Through Amount for Direct Medical Education' },
        { name: 'passThroughAmountForKidneyAcquisition', label: 'Pass-Through Amount for Kidney Acquisition' },
        { name: 'passThroughAmountForSupplyChain', label: 'Pass-Through Amount for Supply Chain' },
        { name: 'passThroughTotalAmount', label: 'Pass-Through Total Amount' },
        { name: 'ppsBlendYearIndicator', label: 'PPS Blend Year Indicator' },
        { name: 'providerType', label: 'Provider Type' },
        { name: 'soleCommunityOrMedicareDependentHospitalBaseYear', label: 'Sole Community or Medicare Dependent Hospital Base Year' },
        { name: 'specialPaymentIndicator', label: 'Special Payment Indicator' },
        { name: 'specialWageIndex', label: 'Special Wage Index' },
        { name: 'stateCode', label: 'State Code' },
        { name: 'supplementalSecurityIncomeRatio', label: 'Supplemental Security Income Ratio' },
        { name: 'supplementalWageIndex', label: 'Supplemental Wage Index' },
        { name: 'supplementalWageIndexIndicator', label: 'Supplemental Wage Index Indicator' },
        { name: 'temporaryReliefIndicator', label: 'Temporary Relief Indicator' },
        { name: 'uncompensatedCareAmount', label: 'Uncompensated Care Amount' },
        { name: 'vbpAdjustment', label: 'VBP Adjustment' },
        { name: 'vbpParticipantIndicator', label: 'VBP Participant Indicator' },
        { name: 'waiverIndicator', label: 'Waiver Indicator' }
    ];
    

    const fields = useMemo(() => [
        createNumberDataField('id', 'Id', { sortable: true, hideFromGrid: true, hideFromFilter: true, equators: integerEquators, width: '200px' }),
        createStringDataField('providerCcn', 'Provider CCN', { sortable: true, equators: StringEquators, onClick: true,
            renderCell: (params) => (
                <Link
                    component="button"
                    variant="body2"
                    color="primary"
                    underline="hover"
                    onClick={() => handleOpen(params.row)}
                    style={{ cursor: "pointer" }}
                >
                {params.value}
                </Link>
            ),
            }),
        createDateField('effectiveDate', 'Effective Date', { sortable: true, equators: dateEquators, width: '200px' }),
        createStringDataField('nationalProviderIdentifier', 'National Provider Identifier', { sortable: true, equators: StringEquators, width: '200px' }),
        createNumberDataField('bedSize', 'Bed Size', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('bundleModel1Discount', 'Bundle Model1 Discount', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('capitalCostToChargeRatio', 'Capital Cost To Charge Ratio', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('capitalExceptionPaymentRate', 'Capital Exception Payment Rate', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('capitalIndirectMedicalEducationRatio', 'Capital Indirect Medical Education Ratio', { sortable: true, equators: integerEquators, width: '200px' }),
        createStringDataField('capitalPpsPaymentCode', 'Capital PPS Payment Code', { sortable: true, equators: StringEquators, width: '200px' }),
        createNumberDataField('caseMixAdjustedCostPerDischargePpsFacilitySpecificRate', 'Case Mix Adjusted Cost Per Discharge PPS Facility Specific Rate', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('caseMixIndex', 'Case Mix Index', { sortable: true, equators: integerEquators, width: '200px' }),
        createStringDataField('cbsaActualGeographicLocation', 'CBSA Actual Geographic Location', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('cbsaStandardizedAmountLocation', 'CBSA Standardized Amount Location', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('cbsaWageIndexLocation', 'CBSA Wage Index Location', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('censusDivision', 'Census Division', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('changeCodeForLugarReclassification', 'Change Code for Lugar Reclassification', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('changeCodeWageIndexReclassification', 'Change Code Wage Index Reclassification', { sortable: true, equators: StringEquators, width: '200px' }),
        createNumberDataField('costOfLivingAdjustment', 'Cost of Living Adjustment', { sortable: true, equators: integerEquators, width: '200px' }),
        createStringDataField('countyCode', 'County Code', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('ehrReductionIndicator', 'EHR Reduction Indicator', { sortable: true, equators: StringEquators, width: '200px' }),
        createDateField('exportDate', 'Export Date', { sortable: true, equators: dateEquators, width: '200px' }),
        createStringDataField('federalPpsBlend', 'Federal PPS Blend', { sortable: true, equators: StringEquators, width: '200px' }),
        createDateField('fiscalYearBeginDate', 'Fiscal Year Begin Date', { sortable: true, equators: dateEquators, width: '200px' }),
        createDateField('fiscalYearEndDate', 'Fiscal Year End Date', { sortable: true, equators: dateEquators, width: '200px' }),
        createStringDataField('hacReductionParticipantIndicator', 'HAC Reduction Participant Indicator', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('hospitalQualityIndicator', 'Hospital Quality Indicator', { sortable: true, equators: StringEquators, width: '200px' }),
        createNumberDataField('hospitalSpecificCapitalRate', 'Hospital Specific Capital Rate', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('hrrAdjustment', 'HRR Adjustment', { sortable: true, equators: integerEquators, width: '200px' }),
        createStringDataField('hrrParticipantIndicator', 'HRR Participant Indicator', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('intermediaryNumber', 'Intermediary Number', { sortable: true, equators: StringEquators, width: '200px' }),
        createNumberDataField('internsToBedsRatio', 'Interns to Beds Ratio', { sortable: true, equators: integerEquators, width: '200px' }),
        createDateField('lastUpdated', 'Last Updated', { sortable: true, equators: dateEquators, width: '200px' }),
        createNumberDataField('lowVolumeAdjustmentFactor', 'Low Volume Adjustment Factor', { sortable: true, equators: integerEquators, width: '200px' }),
        createStringDataField('ltchDppIndicator', 'LTCH DPP Indicator', { sortable: true, equators: StringEquators, width: '200px' }),
        createNumberDataField('medicaidRatio', 'Medicaid Ratio', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('medicarePerformanceAdjustment', 'Medicare Performance Adjustment', { sortable: true, equators: integerEquators, width: '200px' }),
        createStringDataField('msaActualGeographicLocation', 'MSA Actual Geographic Location', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('msaStandardizedAmountLocation', 'MSA Standardized Amount Location', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('msaWageIndexLocation', 'MSA Wage Index Location', { sortable: true, equators: StringEquators, width: '200px' }),
        createNumberDataField('newCapitalHoldHarmlessRate', 'New Capital Hold Harmless Rate', { sortable: true, equators: integerEquators, width: '200px' }),
        createStringDataField('newHospital', 'New Hospital', { sortable: true, equators: StringEquators, width: '200px' }),
        createNumberDataField('oldCapitalHoldHarmlessRate', 'Old Capital Hold Harmless Rate', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('operatingCostToChargeRatio', 'Operating Cost to Charge Ratio', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('passThroughAmountForAllogenicStemCellAcquisition', 'Pass-Through Amount for Allogenic Stem Cell Acquisition', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('passThroughAmountForDirectGraduateMedicalEducation', 'Pass-Through Amount for Direct Graduate Medical Education', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('passThroughAmountForDirectMedicalEducation', 'Pass-Through Amount for Direct Medical Education', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('passThroughAmountForKidneyAcquisition', 'Pass-Through Amount for Kidney Acquisition', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('passThroughAmountForSupplyChain', 'Pass-Through Amount for Supply Chain', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('passThroughTotalAmount', 'Pass-Through Total Amount', { sortable: true, equators: integerEquators, width: '200px' }),
        createStringDataField('ppsBlendYearIndicator', 'PPS Blend Year Indicator', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('providerType', 'Provider Type', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('soleCommunityOrMedicareDependentHospitalBaseYear', 'Sole Community or Medicare Dependent Hospital Base Year', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('specialPaymentIndicator', 'Special Payment Indicator', { sortable: true, equators: StringEquators, width: '200px' }),
        createNumberDataField('specialWageIndex', 'Special Wage Index', { sortable: true, equators: integerEquators, width: '200px' }),
        createStringDataField('stateCode', 'State Code', { sortable: true, equators: StringEquators, width: '200px' }),
        createNumberDataField('supplementalSecurityIncomeRatio', 'Supplemental Security Income Ratio', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('supplementalWageIndex', 'Supplemental Wage Index', { sortable: true, equators: integerEquators, width: '200px' }),
        createStringDataField('supplementalWageIndexIndicator', 'Supplemental Wage Index Indicator', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('temporaryReliefIndicator', 'Temporary Relief Indicator', { sortable: true, equators: StringEquators, width: '200px' }),
        createNumberDataField('uncompensatedCareAmount', 'Uncompensated Care Amount', { sortable: true, equators: integerEquators, width: '200px' }),
        createNumberDataField('vbpAdjustment', 'VBP Adjustment', { sortable: true, equators: integerEquators, width: '200px' }),
        createStringDataField('vbpParticipantIndicator', 'VBP Participant Indicator', { sortable: true, equators: StringEquators, width: '200px' }),
        createStringDataField('waiverIndicator', 'Waiver Indicator', { sortable: true, equators: StringEquators, width: '200px' })
    ], []);


    const tableFilter = useGridFilters(),
        pageRecord = tableFilter.pagination;

    useEffect(() => {
        const pageName = {
            name: 'provider-data-landing'
        }
        resetCriteriaWaiting(pageName.name)

        return function cleanup() {
            searchReset();
            resetPagedTable(pageName.name);
        }
    }, [searchGetByName]);

    function handleLoad(sort, filter, pagination) {
        providerDataLanding(pagination, sort, filter);
    }

    function handleRefresh() {
        setRefresh(Promise.resolve());
    }

    return (
        <>
            <Helmet title="CCN Data" />
            <Portlet>
                <PortletHeader
                    title="CCN Data"
                    name="provider-data-landing"
                    handleRefresh={handleRefresh}
                    showSearchFilters={true}
                    showSearchFiltersDropdown={false}
                />
                <PortletBody>
                    <Stack direction="column" spacing={1} sx={{ height: '100%', minHeight: 400 }}>
                        <TableLevelAction
                            handleRefresh={handleRefresh}
                            actionLevelGrid={ () =>
                                <Grid container>
                                    <Grid item md={12}>
                                    <h6 className="">{`Total Records : ${(providerData?.length > 0 && pageRecord?.totalRecords) ? pageRecord?.totalRecords : 0}`}</h6>
                                    </Grid>
                                </Grid>
                            }
                        />
                        <XDataGrid
                            fields={fields}
                            data={providerData}
                            loading={pending}
                            showSearchFilters={true}
                            paginationFilters={ tableFilter }
                            onLoad={handleLoad}
                            refresh={refresh}
                            name="provider-data-landing"
                            defaultSort={DEFAULT_SORT}
                            defaultHiddenFields={[]}
                            criteriasSaveEnabled={false}
                        />
                    </Stack>
                </PortletBody>
            </Portlet>

            <Dialog maxWidth='lg' open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle sx={{borderBottom: '1px solid rgba(0, 0, 0, 0.1)'}}>Provider Details</DialogTitle>
                <DialogContent>
                    {selectedRowData ? (
                        <Grid container spacing={2}>
                            {Object.keys(selectedRowData).map((fieldName, index) => {
                                
                                const fieldDef = fieldDefinitions?.find(field => fieldName !== "id" && field.name === fieldName);
                                if (!fieldDef) return null;
                                let dateFieldNames = ["effectiveDate", "fiscalYearBeginDate", "exportDate", "fiscalYearEndDate", "lastUpdated"];
                                let isDateType = dateFieldNames.includes(fieldName);
                                    
                                return(
                                    <Grid item xs={4} key={index}>
                                        <TextField
                                            type={isDateType ? "date" : "text"}
                                            label={fieldDef?.label}
                                            value={selectedRowData[fieldName] ?? ""}
                                            fullWidth
                                            margin="normal"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    </Grid>
                                )}
                            )}
                        </Grid>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{borderTop: '1px solid rgba(0, 0, 0, 0.1)'}}>
                    <Button onClick={handleCloseDialog} variant="contained" color="primary">Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}